import React from 'react';
import { i18n, MessageDescriptor } from '@lingui/core';
import { defineMessage, t } from '@lingui/macro';

import { Boarding, Daycare, DogTraining, DropIn, Sitting, Walking } from '@rover/icons';
import { captureMessage, withScope } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { ServiceTypeSlugs } from '@rover/types/src/Service';
import type { ServiceTypeSlug } from '@rover/types/src/ServiceType';

const inSittersHome = (): string =>
  t({
    message: `in the sitter's home`,
    comment:
      'Describes a service type as in "Boarding *in the sitter\'s home*". This copy is displayed on a separate line from the service type name.',
  });

const inYourHome = (): string =>
  t({
    message: `in your home`,
    comment:
      'Describes a service type as in "House Sitting *in your home*". This is displayed on a separate line from the service type name.',
  });

const inNeighborhood = (): string =>
  t({
    message: `in your neighborhood`,
    comment:
      'Describes the "Dog Walking" service as in "Dog Walking in your neighborhood". This is displayed on a separate line from the service type name.',
  });

const oneOnOneVirtualSessions = (): string =>
  t({
    message: `1-1 virtual sessions through GoodPup`,
    comment:
      'Describes the "Dog Training" service as in "1-1 virtual training sessions through GoodPup". This is displayed on a separate line from the service type name.',
  });

export default class ServiceTypeDescription {
  static ICONS: { [serviceTypeSlug: string]: string } = {
    [ServiceTypeSlugs.overnightBoarding]: Boarding,
    [ServiceTypeSlugs.overnightTraveling]: Sitting,
    [ServiceTypeSlugs.dogWalking]: Walking,
    [ServiceTypeSlugs.dropIn]: DropIn,
    [ServiceTypeSlugs.doggyDayCare]: Daycare,
    [ServiceTypeSlugs.dogTraining]: DogTraining,
  };

  static NAMES: { [serviceTypeSlug: string]: MessageDescriptor } = {
    /*
       Some of these are different from the DB so we can’t
       rely on a serialized version of them and we need these
       on the frontend for the defaults anyway so they’ll
       live here for the time being.
    */
    [ServiceTypeSlugs.overnightBoarding]: defineMessage({ id: 'Boarding' }),
    [ServiceTypeSlugs.overnightTraveling]: defineMessage({ id: 'House Sitting' }),
    [ServiceTypeSlugs.dogWalking]: defineMessage({ id: 'Dog Walking' }),
    [ServiceTypeSlugs.dropIn]: defineMessage({ id: 'Drop-ins' }),
    [ServiceTypeSlugs.doggyDayCare]: defineMessage({ id: 'Doggy Day Care' }),
    [ServiceTypeSlugs.dogTraining]: defineMessage({ id: 'Dog Training' }),
  };

  static DESCRIPTIONS = (): { [serviceTypeSlug: string]: string } => ({
    [ServiceTypeSlugs.overnightBoarding]: inSittersHome(),
    [ServiceTypeSlugs.overnightTraveling]: inYourHome(),
    [ServiceTypeSlugs.dogWalking]: inNeighborhood(),
    [ServiceTypeSlugs.dropIn]: inYourHome(),
    [ServiceTypeSlugs.doggyDayCare]: inSittersHome(),
    [ServiceTypeSlugs.dogTraining]: oneOnOneVirtualSessions(),
  });

  static TARGETS: { [serviceTypeSlug: string]: string } = {
    [ServiceTypeSlugs.dogTraining]: '_blank',
  };

  static CALLOUTS: { [serviceTypeSlug: string]: string } = {};

  slug: ServiceTypeSlug;

  name: string | null;

  description: string | null;

  webAction: string;

  webActionTarget: string | undefined;

  callout: string | null;

  constructor({
    slug,
    webAction,
    description,
  }: {
    slug: ServiceTypeSlug;
    webAction?: string;
    description?: string;
  }) {
    this.slug = slug;

    if (!webAction) {
      this.webAction = encodeURI(`/search/?pet_type=dog,cat&service_type=${this.slug}`);
    } else {
      this.webAction = webAction;
    }

    this.webActionTarget = ServiceTypeDescription.TARGETS[this.slug] || undefined;

    this.description = description || ServiceTypeDescription.DESCRIPTIONS()[this.slug];
    if (!this.description) {
      this.reportMissingComponentError('description');
      this.description = null;
    }

    this.name = i18n._(/* i18n */ ServiceTypeDescription.NAMES[this.slug]);
    if (!this.name) {
      this.reportMissingComponentError('name');
      this.name = null;
    }

    // Not all service types have callouts so no need to report an error if we can't find one
    this.callout = ServiceTypeDescription.CALLOUTS[this.slug] || null;
  }

  /**
   * Unfortunately we cannot put the Icon on the ServiceTypeDescription
   * instance itself because in the process of SSR, the function is no longer
   * serializable and gets lost during the handover to the client
   */
  static renderIcon(slug: ServiceTypeSlug): string | JSX.Element {
    const Icon = ServiceTypeDescription.ICONS[slug];
    return Icon && <Icon />;
  }

  reportMissingComponentError(componentName: string): void {
    withScope((scope) => {
      scope.setExtras({ componentName, serviceType: this.slug });
      captureMessage('Missing ServiceTypeDescription component');
    });
  }
}
